/**
 * Pure helpers for the owner's Sessions & security page (/sessions).
 * Session docs come from `sessions/{id}` — see src/utils/deviceSession.js.
 */

export const ONLINE_MS = 35 * 60 * 1000;      // heartbeat is every 30 min while open
const RECENT_MS = 7 * 24 * 60 * 60 * 1000;
const NEW_MS = 24 * 60 * 60 * 1000;           // "New sign-in" badge
const INACTIVE_MS = 30 * 24 * 60 * 60 * 1000; // Security checkup: stale device
/** Accounts always listed on the page, even when signed in nowhere. */
export const STAFF_ROLES = ['owner', 'admin', 'manager', 'employee', 'security'];

/** Firestore Timestamp | Date | ms | null → ms (0 when unknown). */
export function toMillis(t) {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (t instanceof Date) return t.getTime();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  return 0;
}

/**
 * 'online' | 'recent' | 'idle' | 'pending'
 * pending = owner signed it out; waiting for the device to come online.
 */
export function sessionStatus(s, now) {
  if (s.revoked) return 'pending';
  const age = now - toMillis(s.lastSeenAt);
  if (age <= ONLINE_MS) return 'online';
  if (age <= RECENT_MS) return 'recent';
  return 'idle';
}

/**
 * Sessions worth showing. A revoked session whose tokens were revoked too is
 * already signed out (within the hour, instantly if it was open) — hide it.
 */
function visibleSessions(list) {
  return list.filter((s) => !(s.revoked && s.tokensRevoked));
}

/** Group by account → [{ uid, name, email, role, sessions, activeCount, lastSeen }]. */
export function groupSessions(list, now) {
  const byUid = new Map();
  for (const s of visibleSessions(list)) {
    if (!s.uid) continue;
    let g = byUid.get(s.uid);
    if (!g) {
      g = { uid: s.uid, name: s.name || s.email || 'Unknown', email: s.email || '', role: s.role || 'customer', sessions: [], activeCount: 0, lastSeen: 0 };
      byUid.set(s.uid, g);
    }
    g.sessions.push(s);
    if (!s.revoked) g.activeCount++;
    g.lastSeen = Math.max(g.lastSeen, toMillis(s.lastSeenAt));
  }
  const rank = { online: 0, recent: 1, idle: 2, pending: 3 };
  const groups = [...byUid.values()];
  for (const g of groups) {
    g.sessions.sort((a, b) =>
      rank[sessionStatus(a, now)] - rank[sessionStatus(b, now)]
      || toMillis(b.lastSeenAt) - toMillis(a.lastSeenAt));
  }
  return groups.sort((a, b) => b.lastSeen - a.lastSeen || a.name.localeCompare(b.name));
}

/** { people, devices, online } counting only sessions still signed in. */
export function summarize(groups, now) {
  let people = 0, devices = 0, online = 0;
  for (const g of groups) {
    if (g.activeCount) people++;
    for (const s of g.sessions) {
      if (s.revoked) continue;
      devices++;
      if (sessionStatus(s, now) === 'online') online++;
    }
  }
  return { people, devices, online };
}

/**
 * Device is running an older build than the one deployed. Devices that
 * registered before versions were reported have none — also old by definition.
 * Unknown `latest` (dev build, fetch failed) → never flag.
 */
export function isOutdated(s, latest) {
  if (!latest || s.revoked) return false;
  return (s.device?.version || null) !== latest;
}

export function relativeTime(ms, now) {
  if (!ms) return 'unknown';
  const d = Math.max(0, now - ms);
  const min = Math.round(d / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function placeOf(loc) {
  if (!loc) return 'Unknown location';
  const parts = [loc.city, loc.region, loc.country].filter(Boolean);
  const unique = parts.filter((p, i) => parts.indexOf(p) === i);
  if (unique.length) return unique.join(', ');
  return loc.ip ? `IP ${loc.ip}` : 'Unknown location';
}

// ── People (every staff account + anyone with a device) ─────────────────────

export const isNew = (s, now) => !s.revoked && !!toMillis(s.createdAt) && now - toMillis(s.createdAt) <= NEW_MS;
export const isInactive = (s, now) => !s.revoked && now - toMillis(s.lastSeenAt) > INACTIVE_MS;

/**
 * Merge `users` docs with session groups. Profile fields (name, role, blocked,
 * temporary password) come from `users` — it is the source of truth; people
 * with no users doc (customers) still appear through their sessions.
 * Signed-in people first (freshest first), then the rest by name.
 */
export function buildPeople(users, sessions, now) {
  const byUid = new Map(groupSessions(sessions, now).map((g) => [g.uid, g]));
  for (const u of users) {
    if (!u.uid) continue;
    const g = byUid.get(u.uid) || { uid: u.uid, sessions: [], activeCount: 0, lastSeen: 0 };
    byUid.set(u.uid, {
      ...g,
      name: u.name || g.name || u.email || 'Unknown',
      email: u.email || g.email || '',
      role: u.role || g.role || 'customer',
      disabled: !!u.disabled,
      mustChangePassword: !!u.mustChangePassword,
    });
  }
  return [...byUid.values()].sort((a, b) =>
    (b.activeCount > 0) - (a.activeCount > 0)
    || b.lastSeen - a.lastSeen
    || (a.name || '').localeCompare(b.name || ''));
}

export const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'online', label: 'Online now' },
  { key: 'new', label: 'New sign-ins' },
  { key: 'outdated', label: 'Old version' },
  { key: 'inactive', label: 'Inactive 30d+' },
  { key: 'signedout', label: 'Not signed in' },
  { key: 'blocked', label: 'Blocked' },
];

function matchesFilter(p, filter, now, latest) {
  switch (filter) {
    case 'online': return p.sessions.some((s) => sessionStatus(s, now) === 'online');
    case 'new': return p.sessions.some((s) => isNew(s, now));
    case 'outdated': return p.sessions.some((s) => isOutdated(s, latest));
    case 'inactive': return p.sessions.some((s) => isInactive(s, now));
    case 'signedout': return p.activeCount === 0;
    case 'blocked': return !!p.disabled;
    default: return true;
  }
}

const words = (q) => (q || '').trim().toLowerCase().split(/\s+/).filter(Boolean);

function deviceHay(s) {
  const d = s.device || {};
  return [s.nickname, d.label, d.platform, d.browser, placeOf(s.location), s.location?.ip]
    .filter(Boolean).join(' ').toLowerCase();
}

/** Does this device match every search word (on its own)? */
export function deviceMatchesQuery(s, q) {
  const w = words(q);
  if (!w.length) return false;
  const hay = deviceHay(s);
  return w.every((x) => hay.includes(x));
}

/** Free-text search over name, email, role and every device's name, kind, browser, place and IP. */
export function matchesQuery(p, q) {
  const w = words(q);
  if (!w.length) return true;
  const hay = [p.name, p.email, p.role, ...p.sessions.map(deviceHay)].filter(Boolean).join(' ').toLowerCase();
  return w.every((x) => hay.includes(x));
}

/**
 * Split text into [{ text, hit }] around the search words, for <mark>ing
 * matches. Case-insensitive; overlapping words merge.
 */
export function highlightParts(text, q) {
  const str = String(text ?? '');
  const w = words(q);
  if (!w.length || !str) return [{ text: str, hit: false }];
  const lower = str.toLowerCase();
  const on = new Array(str.length).fill(false);
  for (const x of w) {
    for (let i = lower.indexOf(x); i !== -1; i = lower.indexOf(x, i + 1)) on.fill(true, i, i + x.length);
  }
  const out = [];
  for (let i = 0; i < str.length; i++) {
    const last = out[out.length - 1];
    if (last && last.hit === on[i]) last.text += str[i];
    else out.push({ text: str[i], hit: on[i] });
  }
  return out;
}

export function filterPeople(people, { q = '', filter = 'all', role = 'all' } = {}, now, latest) {
  return people.filter((p) => (role === 'all' || p.role === role)
    && matchesFilter(p, filter, now, latest)
    && matchesQuery(p, q));
}

export function filterCounts(people, now, latest) {
  const out = {};
  for (const f of FILTERS) out[f.key] = people.filter((p) => matchesFilter(p, f.key, now, latest)).length;
  return out;
}

/**
 * Google-style security checkup. Returns only what needs attention:
 *   staleSessionIds — devices unused for 30+ days (never this device)
 *   outdated        — devices on an old app build
 *   mustChange      — people still on a temporary password
 *   blocked         — blocked accounts
 */
export function checkup(people, now, latest, mySessionId) {
  const staleSessionIds = [];
  let outdated = 0;
  for (const p of people) {
    for (const s of p.sessions) {
      if (s.id !== mySessionId && isInactive(s, now)) staleSessionIds.push(s.id);
      if (isOutdated(s, latest)) outdated++;
    }
  }
  return {
    staleSessionIds,
    outdated,
    mustChange: people.filter((p) => p.mustChangePassword && !p.disabled),
    blocked: people.filter((p) => p.disabled).length,
  };
}

export function formatDateTime(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Two-letter avatar initials. */
export function initialsOf(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const two = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2);
  return two.toUpperCase();
}

// ── Activity (securityEvents) ───────────────────────────────────────────────

export const EVENT_GROUPS = [
  { key: 'all', label: 'All activity', types: null },
  { key: 'signin', label: 'Sign-ins', types: ['signin'] },
  { key: 'signout', label: 'Sign-outs', types: ['signout-device', 'signout-devices', 'signout-user', 'signout-own-others', 'signout-all'] },
  { key: 'password', label: 'Passwords', types: ['password-set', 'password-reset-email', 'password-changed'] },
  { key: 'block', label: 'Blocks', types: ['blocked', 'unblocked'] },
  { key: 'device', label: 'Devices', types: ['device-renamed'] },
];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** { icon, text, tone } for one securityEvents doc. */
export function describeEvent(e) {
  const actor = e.actorName || 'Someone';
  const target = e.targetName || 'an account';
  const n = typeof e.count === 'number' ? e.count : null;
  switch (e.type) {
    case 'signin': return { icon: '🟢', tone: 'info', text: `${actor} signed in${e.device ? ` on ${e.device}` : ''}` };
    case 'signout-device': return { icon: '⎋', tone: 'warn', text: `${actor} signed out ${target}${e.device ? ` on ${e.device}` : ''}` };
    case 'signout-devices': return { icon: '🧹', tone: 'warn', text: `${actor} signed out ${plural(n ?? 0, 'inactive device')}` };
    case 'signout-user': return { icon: '⎋', tone: 'warn', text: `${actor} signed out ${target} on all devices` };
    case 'signout-own-others': return { icon: '⎋', tone: 'warn', text: `${actor} signed out their other devices` };
    case 'signout-all': return { icon: '🚨', tone: 'danger', text: `${actor} signed out everyone${n != null ? ` (${plural(n, 'account')})` : ''}` };
    case 'password-set': return { icon: '🔑', tone: 'warn', text: `${actor} set a new password for ${target}` };
    case 'password-reset-email': return { icon: '✉️', tone: 'info', text: `${actor} sent a password reset email to ${target}` };
    case 'password-changed': return { icon: '🔑', tone: 'info', text: `${actor} changed their password` };
    case 'blocked': return { icon: '🚫', tone: 'danger', text: `${actor} blocked sign-in for ${target}` };
    case 'unblocked': return { icon: '✅', tone: 'info', text: `${actor} unblocked ${target}` };
    case 'device-renamed': return { icon: '✏️', tone: 'info', text: `${actor} renamed ${target}’s device${e.device ? ` to “${e.device}”` : ''}` };
    default: return { icon: '•', tone: 'info', text: `${actor}: ${e.type}` };
  }
}

/** 'Today' | 'Yesterday' | 'Mon, Sep 28' — for grouping the activity feed. */
export function dayLabel(ms, now) {
  const d = new Date(ms); const t = new Date(now);
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(t) - startOf(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
